import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiEdit2, FiTrash2 } from 'react-icons/fi';
import { ConfirmationDialog, PageHeader, useToast } from 'components/admin';
import { Button, Input, Select, Table } from 'components/common';
import {
    deleteProjectInformationType,
    getAdminProjectInformationTypes,
} from 'apis/adminProjectInformationTypes';
import { normalizeUploadedImageUrl } from 'apis/uploadImage';
import { useActiveLanguagesQuery, useAdminDeleteMutation, useAdminListQuery } from 'hooks';
import '../../../styles/admin-shared.scss';

const ProjectInformationTypesList = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(10);
    const [keyword, setKeyword] = useState('');
    const [isActiveFilter, setIsActiveFilter] = useState('');
    const [languageCodeFilter, setLanguageCodeFilter] = useState('vi');
    const [deleteId, setDeleteId] = useState(null);
    const toast = useToast();
    const navigate = useNavigate();

    const queryParams = useMemo(() => ({
        pageIndex: currentPage,
        pageSize,
        keyword: keyword || undefined,
        isActive: isActiveFilter === '' ? undefined : isActiveFilter === 'true',
        languageCode: languageCodeFilter || undefined,
    }), [currentPage, pageSize, keyword, isActiveFilter, languageCodeFilter]);

    const { data = { items: [], total: 0, currentPage, pageSize }, isFetching, error } = useAdminListQuery(
        'project-information-types',
        queryParams,
        getAdminProjectInformationTypes,
    );
    const { data: languages = [] } = useActiveLanguagesQuery();
    const deleteMutation = useAdminDeleteMutation('project-information-types', deleteProjectInformationType);

    useEffect(() => {
        setCurrentPage(1);
    }, [keyword, isActiveFilter, languageCodeFilter]);

    useEffect(() => {
        if (error) toast.error(error.message || 'Tải dữ liệu thất bại');
    }, [error, toast]);

    const handleDelete = async () => {
        try {
            const res = await deleteMutation.mutateAsync(deleteId);
            if (res.success) toast.success('Khóa loại thông tin thành công');
            else toast.error(res.message || 'Khóa loại thông tin thất bại');
        } catch {
            toast.error('Khóa loại thông tin thất bại');
        }
        setDeleteId(null);
    };

    const columns = [
        { field: 'id', header: 'ID' },
        {
            header: 'Icon',
            render: (row) => {
                const iconUrl = normalizeUploadedImageUrl(row.iconURL || row.iconUrl || '');
                return iconUrl ? <img src={iconUrl} alt="" style={{ width: 36, height: 36, objectFit: 'contain' }} /> : '—';
            },
        },
        { field: 'code', header: 'Mã' },
        { header: 'Tên', render: (row) => row.name || row.label || row.translations?.[0]?.name || '—' },
        { field: 'sortOrder', header: 'Thứ tự' },
        { header: 'Trạng thái', render: (row) => <span className={`status-badge ${row.isActive ? 'active' : 'inactive'}`}>{row.isActive ? 'Hoạt động' : 'Không hoạt động'}</span> },
        {
            header: 'Thao tác',
            render: (row) => (
                <div className="action-buttons">
                    <button className="icon-btn icon-btn--edit" onClick={() => navigate(`/admin/project-information-types/edit/${row.id}`)} title="Sửa"><FiEdit2 size={15} /></button>
                    <button className="icon-btn icon-btn--delete" onClick={() => setDeleteId(row.id)} title="Khóa"><FiTrash2 size={15} /></button>
                </div>
            ),
        },
    ];

    return (
        <div className="admin-page">
            <PageHeader
                title="Loại thông tin dự án"
                subtitle="Quản lý các nhãn thông tin lặp lại trong dự án tiêu biểu"
                action={<Button onClick={() => navigate('/admin/project-information-types/create')}>+ Thêm Loại</Button>}
            />
            <div className="filters-bar">
                <div className="filter-item"><Input placeholder="Tìm kiếm..." value={keyword} onChange={e => setKeyword(e.target.value)} /></div>
                <div className="filter-item">
                    <Select value={isActiveFilter} onChange={e => setIsActiveFilter(e.target.value)} options={[{ value: '', label: 'Tất cả trạng thái' }, { value: 'true', label: 'Hoạt động' }, { value: 'false', label: 'Không hoạt động' }]} />
                </div>
                <div className="filter-item">
                    <Select value={languageCodeFilter} onChange={e => setLanguageCodeFilter(e.target.value)} options={languages.map(l => ({ value: l.code, label: l.name }))} />
                </div>
            </div>
            <Table columns={columns} data={data.items} loading={isFetching} pagination={{ currentPage: data.currentPage, totalPages: Math.ceil(data.total / data.pageSize) || 1, onPageChange: setCurrentPage }} />
            <ConfirmationDialog open={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={handleDelete} title="Khóa loại thông tin" message="Bạn có chắc chắn muốn khóa loại thông tin này không?" />
        </div>
    );
};

export default ProjectInformationTypesList;
